import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Search, Upload, Download, Fingerprint, Trash2 } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useBiometricEmployees,
  useEmployeeActions,
  useDevices,
} from '../hooks/useBiometricAdmin';
import { relativeTime } from '../biometricTypes';

/** Employee ↔ PIN mapping, template counts and per-device push / pull. */
export function EmployeeEnrollTable() {
  const { data: employees, isLoading } = useBiometricEmployees();
  const { data: devices } = useDevices(0);
  const { setMapping, pushToDevice, removeFromDevice, pullTemplates } = useEmployeeActions();

  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [targetSn, setTargetSn] = useState('');

  const activeDevices = useMemo(() => (devices ?? []).filter(d => d.is_active), [devices]);
  const effectiveSn = targetSn || activeDevices[0]?.serial_number || '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees ?? [];
    return (employees ?? []).filter(
      e =>
        e.display_name.toLowerCase().includes(q) ||
        e.biometric_id.toLowerCase().includes(q) ||
        (e.department ?? '').toLowerCase().includes(q),
    );
  }, [employees, search]);

  const saveMapping = async (userId: string, current: string) => {
    const next = drafts[userId] ?? current;
    if (next === current) return;
    try {
      await setMapping.mutateAsync({ userId, biometric_id: next.trim() });
      toast.success(next.trim() ? `Mapped to PIN ${next.trim()}.` : 'Mapping cleared.');
      setDrafts(prev => {
        const copy = { ...prev };
        delete copy[userId];
        return copy;
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const act = async (fn: Promise<unknown>, message: string) => {
    try {
      await fn;
      toast.success(message);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  return (
    <Card className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">Employee enrolment</h2>
          <p className="text-sm text-on-surface-variant">
            The PIN is the enroll number on the device. Templates enrolled on any device are stored here
            and can be pushed to the others.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
            <Input
              className="pl-8"
              placeholder="Search name, PIN or department"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="h-10 rounded-md border border-outline-variant bg-surface px-3 text-sm text-on-surface"
            value={effectiveSn}
            onChange={e => setTargetSn(e.target.value)}
          >
            {activeDevices.length === 0 && <option value="">No active device</option>}
            {activeDevices.map(d => (
              <option key={d.serial_number} value={d.serial_number}>
                {d.name || d.serial_number}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-outline-variant text-left text-xs uppercase tracking-wide text-on-surface-variant">
              <th className="px-2 py-2">Employee</th>
              <th className="px-2 py-2">Department</th>
              <th className="px-2 py-2 w-32">PIN</th>
              <th className="px-2 py-2">Templates</th>
              <th className="px-2 py-2">Last punch</th>
              <th className="px-2 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(emp => {
              const draft = drafts[emp.id] ?? emp.biometric_id;
              const dirty = draft !== emp.biometric_id;
              return (
                <tr key={emp.id} className="border-b border-outline-variant/50 hover:bg-surface-variant/30">
                  <td className="px-2 py-2 font-medium text-on-surface">{emp.display_name}</td>
                  <td className="px-2 py-2 text-on-surface-variant">{emp.department || '—'}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1">
                      <Input
                        className="h-8 w-20"
                        value={draft}
                        placeholder="—"
                        onChange={e => setDrafts(prev => ({ ...prev, [emp.id]: e.target.value }))}
                        onBlur={() => saveMapping(emp.id, emp.biometric_id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveMapping(emp.id, emp.biometric_id);
                        }}
                      />
                      {dirty && <span className="text-xs text-warning">unsaved</span>}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    {emp.template_total === 0 ? (
                      <span className="text-xs text-on-surface-variant">none</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(emp.template_counts).map(([type, count]) => (
                          <Badge key={type} tone="info">
                            {type} × {count}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs text-on-surface-variant">
                    {relativeTime(emp.last_punch_at)}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex justify-end gap-1">
                      <IconButton
                        title="Push this employee to the selected device"
                        disabled={!emp.biometric_id || !effectiveSn}
                        onClick={() =>
                          act(
                            pushToDevice.mutateAsync({ userId: emp.id, sn: effectiveSn }),
                            `Push queued for ${emp.display_name}.`,
                          )
                        }
                      >
                        <Upload className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton
                        title="Ask the device to upload this employee's templates"
                        disabled={!emp.biometric_id || !effectiveSn}
                        onClick={() =>
                          act(
                            pullTemplates.mutateAsync({ userId: emp.id, sn: effectiveSn }),
                            'Template request queued.',
                          )
                        }
                      >
                        <Download className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton
                        title="Push to every active device"
                        disabled={!emp.biometric_id}
                        onClick={() =>
                          act(pushToDevice.mutateAsync({ userId: emp.id }), 'Queued on all devices.')
                        }
                      >
                        <Fingerprint className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton
                        title="Remove from the selected device"
                        tone="danger"
                        disabled={!emp.biometric_id || !effectiveSn}
                        onClick={() => {
                          if (!window.confirm(`Remove ${emp.display_name} from ${effectiveSn}?`)) return;
                          act(
                            removeFromDevice.mutateAsync({ userId: emp.id, sn: effectiveSn }),
                            'Removal queued.',
                          );
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <p className="py-8 text-center text-sm text-on-surface-variant">No employees match that search.</p>
      )}
    </Card>
  );
}

function IconButton({
  children,
  title,
  onClick,
  disabled,
  tone,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md border p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        tone === 'danger'
          ? 'border-danger/30 text-danger hover:bg-danger/10'
          : 'border-outline-variant text-on-surface-variant hover:bg-surface-variant'
      }`}
    >
      {children}
    </button>
  );
}
